// frontend/src/components/calendar/CalendarPage.js
// Calendar: hours each engineer / manager logged per day. Hours come from the
// actual hours entered on every status change (hours_log on the server).
//   8:00 or more  -> green
//   less than 8:00 -> orange
// Engineers see their own calendar; the manager tier can switch to anyone.
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
    Box, Paper, Typography, IconButton, Button, TextField, MenuItem, Alert,
    Skeleton, Stack,
} from '@mui/material';
import { ChevronLeft, ChevronRight } from '@mui/icons-material';

import { getCalendar } from '../../services/endpoints';
import { useAuth } from '../../context/AuthContext';
import { isManagerRole, fmtHours } from '../../constants/domain';

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const GREEN = { bg: '#e8f5e9', border: '#a5d6a7', text: '#2e7d32' };
const ORANGE = { bg: '#ffe9d0', border: '#ffcc80', text: '#e65100' };

const pad = (n) => String(n).padStart(2, '0');
const monthKey = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}`;

const CalendarPage = () => {
    const { user } = useAuth();
    const manager = isManagerRole(user?.role);

    const [cursor, setCursor] = useState(() => {
        const n = new Date();
        return new Date(n.getFullYear(), n.getMonth(), 1);
    });
    const [userId, setUserId] = useState('');
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const res = await getCalendar({ month: monthKey(cursor), user_id: userId || undefined });
            setData(res.data);
            setError(null);
        } catch (err) {
            setError(err.response?.data?.message || err.message);
        } finally {
            setLoading(false);
        }
    }, [cursor, userId]);

    useEffect(() => { load(); }, [load]);

    const hoursByDate = useMemo(() => {
        const m = new Map();
        (data?.days || []).forEach(d => m.set(d.date, d.hours));
        return m;
    }, [data]);

    // Leading blanks so day 1 lands under the right weekday, then every day.
    const cells = useMemo(() => {
        const first = new Date(cursor.getFullYear(), cursor.getMonth(), 1);
        const count = new Date(cursor.getFullYear(), cursor.getMonth() + 1, 0).getDate();
        const out = [];
        for (let i = 0; i < first.getDay(); i++) out.push(null);
        for (let d = 1; d <= count; d++) out.push(d);
        while (out.length % 7 !== 0) out.push(null);
        return out;
    }, [cursor]);

    const todayStr = (() => {
        const n = new Date();
        return `${n.getFullYear()}-${pad(n.getMonth() + 1)}-${pad(n.getDate())}`;
    })();
    const threshold = data?.threshold_hours ?? 8;
    const totalHours = (data?.days || []).reduce((a, d) => a + d.hours, 0);

    return (
        <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                <Box>
                    <Typography variant="h5" fontWeight={700}>Calendar</Typography>
                    <Typography variant="body2" color="text.secondary">
                        Hours logged per day{data?.user ? ` — ${data.user.full_name}` : ''}
                    </Typography>
                </Box>
                {manager && (
                    <TextField select size="small" label="Engineer" sx={{ minWidth: 220 }}
                        value={userId || user?.id || ''}
                        onChange={(e) => setUserId(e.target.value)}>
                        {(data?.engineers || []).map(e => (
                            <MenuItem key={e.id} value={e.id}>{e.full_name}</MenuItem>
                        ))}
                    </TextField>
                )}
            </Box>

            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

            <Paper sx={{ p: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5, flexWrap: 'wrap', gap: 1 }}>
                    <Stack direction="row" alignItems="center" spacing={0.5}>
                        <IconButton aria-label="Previous month"
                            onClick={() => setCursor(c => new Date(c.getFullYear(), c.getMonth() - 1, 1))}>
                            <ChevronLeft />
                        </IconButton>
                        <Typography variant="h6" fontWeight={600} sx={{ minWidth: 170, textAlign: 'center' }}>
                            {cursor.toLocaleDateString('en-GB', { month: 'long', year: 'numeric' })}
                        </Typography>
                        <IconButton aria-label="Next month"
                            onClick={() => setCursor(c => new Date(c.getFullYear(), c.getMonth() + 1, 1))}>
                            <ChevronRight />
                        </IconButton>
                        <Button size="small" onClick={() => {
                            const n = new Date();
                            setCursor(new Date(n.getFullYear(), n.getMonth(), 1));
                        }}>Today</Button>
                    </Stack>
                    <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
                        <Legend color={GREEN} label={`${threshold}:00 or more`} />
                        <Legend color={ORANGE} label={`Less than ${threshold}:00`} />
                        <Typography variant="body2" color="text.secondary">
                            Month total: <strong>{fmtHours(totalHours) || '00:00'}</strong> h
                        </Typography>
                    </Stack>
                </Box>

                {loading && !data ? (
                    <Skeleton variant="rounded" height={380} />
                ) : (
                    <Box sx={{
                        display: 'grid', gridTemplateColumns: 'repeat(7, minmax(0, 1fr))',
                        border: '1px solid', borderColor: 'divider', borderRadius: 1, overflow: 'hidden',
                    }}>
                        {WEEKDAYS.map(w => (
                            <Box key={w} sx={{
                                p: 0.75, fontSize: 12, fontWeight: 700, color: 'text.secondary',
                                borderBottom: '1px solid', borderColor: 'divider', bgcolor: 'action.hover',
                            }}>{w}</Box>
                        ))}
                        {cells.map((day, i) => {
                            if (day === null) {
                                return <Box key={`b${i}`} sx={{ minHeight: 84, borderTop: '1px solid', borderRight: '1px solid', borderColor: 'divider', bgcolor: 'action.hover', opacity: 0.4 }} />;
                            }
                            const dateStr = `${cursor.getFullYear()}-${pad(cursor.getMonth() + 1)}-${pad(day)}`;
                            const hrs = hoursByDate.get(dateStr);
                            const has = hrs !== undefined && hrs > 0;
                            const tone = has ? (hrs >= threshold ? GREEN : ORANGE) : null;
                            const isToday = dateStr === todayStr;
                            return (
                                <Box key={dateStr} sx={{
                                    minHeight: 84, p: 0.75, display: 'flex', flexDirection: 'column',
                                    borderTop: '1px solid', borderRight: '1px solid', borderColor: tone ? tone.border : 'divider',
                                    bgcolor: tone ? tone.bg : 'transparent',
                                    outline: isToday ? '2px solid #1e88e5' : 'none', outlineOffset: -2,
                                }}>
                                    <Typography variant="caption" sx={{
                                        fontWeight: isToday ? 700 : 500,
                                        color: tone ? tone.text : 'text.secondary',
                                    }}>{day}</Typography>
                                    {has && (
                                        <Typography sx={{ m: 'auto', fontWeight: 700, fontSize: 16, color: tone.text }}>
                                            {fmtHours(hrs)}
                                        </Typography>
                                    )}
                                </Box>
                            );
                        })}
                    </Box>
                )}
            </Paper>
        </Box>
    );
};

const Legend = ({ color, label }) => (
    <Stack direction="row" alignItems="center" spacing={0.75}>
        <Box sx={{ width: 14, height: 14, borderRadius: 0.5, bgcolor: color.bg, border: `1px solid ${color.border}` }} />
        <Typography variant="caption">{label}</Typography>
    </Stack>
);

export default CalendarPage;
